/**
 * Local split/unsplit demonstration seeded by initialTasks. Splitting halves duration into sibling
 * items; unsplitting combines siblings. Changes live in component state and are not persisted to
 * the API.
 *
 * Toggling scissor mode re-renders only the toolbar and the board element: cards are memoized
 * ({@link ScissorCard}), take their mode styling from the board's `data-scissor` attribute through
 * CSS, and read the mode from a ref when a drag or click starts. Cards use ordinary flex layout;
 * pointer dragging and removal animate without measuring a layout projection for every card.
 * Entry animation is disabled so a retained board reveals fully painted cards immediately.
 *
 * @module apps/web/src/components/blocks/ScissorKanban
 */

import { memo, useCallback, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { Scissors, Undo2 } from 'lucide-react';
import { ActivityDone, activityIsDone } from '@/components/activities/ActivityDone';
import type { TaskUI } from '@fluxability/types';
import { Button } from '@/components/ui/button';

/**
 * Local scissor-card data: identifier, title, duration, day, split capability, and optional parent
 * link for recombination.
 */
export interface TaskItem {
  /** Local demo item identifier used to replace or merge split pieces. */
  id: string;
  /** Visible title shared by the split pieces. */
  title: string;
  /** Demo duration divided when splitting and summed when recombining. */
  duration: number;
  /** Day column containing the local demo item. */
  day: string;
  /** Whether scissor mode allows this item to be split. */
  is_splittable: boolean;
  /** Original item identifier used to find sibling pieces for recombination. */
  parentId?: string;
  /** Original canonical activity identity, retained through any depth of local splitting. */
  sourceId?: string;
  /** Canonical lifecycle status carried into the local visual splitting demo. */
  status?: TaskUI['status'];
  /** Canonical completion percentage; 100 is visibly done. */
  completionPercent?: number;
}

/** Required local-demo activities supplied at mount; the splitting demonstration owns its later state. */
interface ScissorKanbanProps {
  /** Required seed activities copied into local component state on mount; subsequent split changes are a local demonstration. */
  initialTasks: TaskItem[];
}

/**
 * Local split/unsplit demonstration seeded by initialTasks. Splitting halves duration into sibling
 * items; unsplitting combines siblings. Changes live in component state and are not persisted to
 * the API.
 */
export function ScissorKanban({ initialTasks }: ScissorKanbanProps) {
  // Canonical completion remains live while split positions/durations stay local to this demo.
  const completed = useMemo(() => new Map(initialTasks.map(task => [task.id, activityIsDone({
    status: task.status ?? 'todo', completionPercent: task.completionPercent,
  })])), [initialTasks]);
  const [tasks, setTasks] = useState<TaskItem[]>(initialTasks);
  const [isScissorMode, setIsScissorMode] = useState(false);
  // Read by the memoized cards at click or drag start, so toggling does not re-render them.
  const modeRef = useRef(isScissorMode);
  modeRef.current = isScissorMode;

  const handleTaskClick = useCallback((task: TaskItem) => {
    if (!modeRef.current || !task.is_splittable) return;

    // Keep animation identities distinct when a card is split again before its old exit ends.
    const splitId = crypto.randomUUID();
    setTasks(prev => {
      // An exiting animated card can still receive a click after its item was removed.
      if (!prev.some(item => item.id === task.id)) return prev;
      // Remove the original
      const filtered = prev.filter(t => t.id !== task.id);

      const halfDuration = task.duration / 2;

      // Create two halves
      const child1: TaskItem = {
        ...task,
        id: `${task.id}-child1-${splitId}`,
        duration: halfDuration,
        parentId: task.id,
        sourceId: task.sourceId ?? task.id,
      };

      const child2: TaskItem = {
        ...task,
        id: `${task.id}-child2-${splitId}`,
        duration: task.duration - halfDuration,
        parentId: task.id,
        sourceId: task.sourceId ?? task.id,
      };

      return [...filtered, child1, child2];
    });
  }, []);

  const handleUnsplit = useCallback((e: React.MouseEvent, childTask: TaskItem) => {
    e.stopPropagation();
    const parentId = childTask.parentId;
    if (!parentId) return;

    setTasks(prev => {
      // Find all siblings
      const siblings = prev.filter(t => t.parentId === parentId);
      const otherTasks = prev.filter(t => t.parentId !== parentId);

      // Calculate total original duration
      const totalDuration = siblings.reduce((sum, t) => sum + t.duration, 0);

      // Recreate parent
      const parentTask: TaskItem = {
        id: parentId,
        sourceId: childTask.sourceId ?? parentId,
        title: childTask.title,
        duration: totalDuration,
        day: childTask.day,
        is_splittable: true,
      };

      return [...otherTasks, parentTask];
    });
  }, []);

  return (
    <div className="flex flex-col gap-4 p-2 sm:gap-6 sm:p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-bold sm:text-xl">Scissor Kanban</h2>
        <Button
          variant={isScissorMode ? 'destructive' : 'outline'}
          onClick={() => setIsScissorMode(!isScissorMode)}
          className="flex items-center gap-2"
          size="sm"
        >
          <Scissors className="h-4 w-4" />
          {isScissorMode ? 'Exit Scissor Mode' : 'Scissor Mode'}
        </Button>
      </div>

      <div
        data-scissor={isScissorMode}
        className={`group/board bg-accent/20 flex min-h-[300px] gap-2 overflow-x-auto rounded-xl p-2 sm:min-h-[400px] sm:gap-4 sm:p-6 ${isScissorMode ? 'cursor-crosshair' : ''}`}
      >
        <ScissorCards
          tasks={tasks}
          completed={completed}
          modeRef={modeRef}
          onSplit={handleTaskClick}
          onUnsplit={handleUnsplit}
        />
      </div>
    </div>
  );
}

/** Inputs for {@link ScissorCards}; the callbacks and ref must be stable for the memo to hold. */
interface ScissorCardsProps {
  /** The local demo items, in board order. */
  tasks: TaskItem[];
  /** Current canonical completion keyed by the original activity, including local split siblings. */
  completed: ReadonlyMap<string, boolean>;
  /** Current scissor mode, read when a drag or click starts. */
  modeRef: MutableRefObject<boolean>;
  /** Split an item. */
  onSplit: (task: TaskItem) => void;
  /** Recombine an item's split siblings. */
  onUnsplit: (e: React.MouseEvent, task: TaskItem) => void;
}

/**
 * The animated card list. Memoized on the item list, so a mode toggle does not re-render
 * AnimatePresence, whose presence context would otherwise reach every card.
 * @component ScissorCardsView
 */
function ScissorCardsView({ tasks, completed, modeRef, onSplit, onUnsplit }: ScissorCardsProps) {
  return (
    <AnimatePresence>
      {tasks.map(task => (
        <ScissorCard
          key={task.id}
          task={task}
          isDone={completed.get(task.sourceId ?? task.parentId ?? task.id) ?? false}
          modeRef={modeRef}
          onSplit={onSplit}
          onUnsplit={onUnsplit}
        />
      ))}
    </AnimatePresence>
  );
}

/** Memoized {@link ScissorCardsView}. */
const ScissorCards = memo(ScissorCardsView);

/** Inputs for {@link ScissorCard}; the callbacks and ref must be stable for the memo to hold. */
interface ScissorCardProps {
  /** The local demo item shown. */
  task: TaskItem;
  /** Current canonical completion of the original activity; shared by local split siblings. */
  isDone: boolean;
  /** Current scissor mode, read when a drag or click starts. */
  modeRef: MutableRefObject<boolean>;
  /** Split the item (ignored outside scissor mode or for unsplittable items). */
  onSplit: (task: TaskItem) => void;
  /** Recombine the item's split siblings. */
  onUnsplit: (e: React.MouseEvent, task: TaskItem) => void;
}

/**
 * One animated demo card. Its mode styling comes from the board's `data-scissor` attribute, and
 * dragging (outside scissor mode only) starts from pointer down through drag controls.
 * @component ScissorCardView
 */
function ScissorCardView({ task, isDone, modeRef, onSplit, onUnsplit }: ScissorCardProps) {
  const dragControls = useDragControls();
  // Splittable cards turn dashed and red on hover in scissor mode; others dim.
  const modeStyle = task.is_splittable
    ? 'group-data-[scissor=true]/board:border-dashed group-data-[scissor=true]/board:bg-transparent group-data-[scissor=true]/board:hover:border-destructive group-data-[scissor=true]/board:hover:bg-destructive/10'
    : 'group-data-[scissor=true]/board:opacity-50 group-data-[scissor=true]/board:grayscale';
  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      drag
      dragListener={false}
      dragControls={dragControls}
      onPointerDown={event => {
        if (!modeRef.current) dragControls.start(event);
      }}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.1}
      onClick={() => onSplit(task)}
      // A fixed-size wrapper with content-visibility: off-screen cards skip restyling their face
      // when the mode flips (the board scrolls sideways through up to hundreds of cards).
      className="relative h-28 w-40 flex-shrink-0 [content-visibility:auto] sm:h-32 sm:w-48"
    >
      <div
        className={`bg-card border-border flex h-full w-full flex-col justify-between rounded-lg border p-2 shadow-sm transition-colors sm:p-4 ${modeStyle}`}
      >
        <div>
          <h3 className={`truncate text-sm font-semibold sm:text-base ${isDone ? 'text-muted-foreground line-through' : ''}`}>{task.title}</h3>
          {isDone && <ActivityDone />}
          <p className="text-muted-foreground text-xs sm:text-sm">{task.duration} min</p>
        </div>
      </div>

      {task.parentId && (
        <Button
          variant="secondary"
          aria-label="Unsplit"
          size="sm"
          className="absolute bottom-1 right-1 h-6 px-1.5 text-xs sm:bottom-2 sm:right-2 sm:h-7 sm:px-2"
          onClick={e => onUnsplit(e, task)}
        >
          <Undo2 className="mr-0.5 h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline">Unsplit</span>
        </Button>
      )}
    </motion.div>
  );
}

/** Memoized {@link ScissorCardView}. */
const ScissorCard = memo(ScissorCardView);
